import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PALETTES, PATTERNS } from "@/contexts/ThemeContext";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  Sun,
  Moon,
  Palette as PaletteIcon,
  Check,
  BookmarkPlus,
  Trash2,
  RefreshCcw,
  Crown,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function ColorField({ label, value, onChange, testId }) {
  const [hex, setHex] = useState(value || "#000000");
  useEffect(() => setHex(value || "#000000"), [value]);

  const commit = (h) => {
    const v = h.startsWith("#") ? h : `#${h}`;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v.toLowerCase());
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
        {label}
      </Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={hex}
          onChange={(e) => {
            setHex(e.target.value);
            onChange(e.target.value);
          }}
          className="size-12 rounded-xl cursor-pointer border border-border bg-transparent p-0 overflow-hidden"
          data-testid={`${testId}-picker`}
          aria-label={`${label} color picker`}
        />
        <Input
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          onBlur={() => commit(hex)}
          onKeyDown={(e) => e.key === "Enter" && commit(hex)}
          placeholder="#8B5CF6"
          maxLength={7}
          className="h-12 font-mono uppercase max-w-[140px]"
          data-testid={`${testId}-hex`}
        />
      </div>
    </div>
  );
}

const PRESETS = [
  { p: "#1f3b5c", b: "#f5ebd7", label: "Navy & cream" },
  { p: "#d67c8c", b: "#faf3e7", label: "Pink & cream" },
  { p: "#7a8047", b: "#f5efdf", label: "Olive & linen" },
  { p: "#8b2e3d", b: "#faebe7", label: "Wine & blush" },
  { p: "#6a8caf", b: "#e9dccb", label: "Light blue & brown" },
  { p: "#b8a8d4", b: "#c8d4b8", label: "Lilac & sage" },
  { p: "#2f7a8a", b: "#ead9c5", label: "Teal & nude" },
  { p: "#2b2b2e", b: "#d8d6d3", label: "Charcoal & soft grey" },
];

export default function ThemePage() {
  const { user, updateSettings } = useAuth();
  const navigate = useNavigate();
  const isPremium = Boolean(user?.is_premium);
  const [savedThemes, setSavedThemes] = useState([]);
  const [newName, setNewName] = useState("");
  const [savingTheme, setSavingTheme] = useState(false);

  const loadSavedThemes = useCallback(async () => {
    try {
      const { data } = await api.get("/custom-themes");
      setSavedThemes(data || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user) loadSavedThemes();
  }, [user, loadSavedThemes]);

  if (!user) return null;

  const palette = user.theme_palette;
  const mode = user.theme_mode;
  const pattern = user.background_pattern || "none";
  const customPrimary = user.custom_primary_hex || "#8b5cf6";
  const customBg = user.custom_background_hex || (mode === "dark" ? "#111111" : "#fafafa");

  const setPalette = (p) => updateSettings({ theme_palette: p });
  const setPattern = (p) => updateSettings({ background_pattern: p });
  const toggleMode = () => updateSettings({ theme_mode: mode === "dark" ? "light" : "dark" });
  const setCustomPrimary = (v) =>
    updateSettings({ theme_palette: "custom", custom_primary_hex: v });
  const setCustomBg = (v) =>
    updateSettings({ theme_palette: "custom", custom_background_hex: v });
  const applyPreset = (preset) =>
    updateSettings({
      theme_palette: "custom",
      custom_primary_hex: preset.p,
      custom_background_hex: preset.b,
    });

  const saveCurrentAsTheme = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Give your theme a name first.");
      return;
    }
    if (!isPremium) {
      toast("Saving custom themes is a Premium feature", {
        description: "Start your 14-day free trial to save unlimited combos.",
        action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
      });
      return;
    }
    setSavingTheme(true);
    try {
      const { data } = await api.post("/custom-themes", {
        name,
        primary_hex: customPrimary,
        background_hex: customBg,
      });
      setSavedThemes((prev) => [...prev, data]);
      setNewName("");
      toast.success("Theme saved", { description: name });
    } catch (e) {
      if (e?.response?.status === 402) {
        toast("Premium feature", {
          description: "Saving custom theme combos requires Premium.",
          action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
        });
      } else {
        toast.error("Could not save theme", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    } finally {
      setSavingTheme(false);
    }
  };

  const deleteSavedTheme = async (id) => {
    if (!window.confirm("Delete this saved theme?")) return;
    try {
      await api.delete(`/custom-themes/${id}`);
      setSavedThemes((prev) => prev.filter((t) => t.id !== id));
      toast.success("Theme deleted");
    } catch (e) {
      toast.error("Could not delete", { description: e?.response?.data?.detail || e.message });
    }
  };

  const applySavedTheme = (theme) =>
    updateSettings({
      theme_palette: "custom",
      custom_primary_hex: theme.primary_hex,
      custom_background_hex: theme.background_hex,
    });

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="theme-back-button"
          >
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <PaletteIcon strokeWidth={1.5} className="size-3.5" />
            Appearance
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Theme
          </h1>
          <p className="text-base text-muted-foreground">
            Pick a palette, switch between light and dark, or design your own.
          </p>
        </div>

        {/* Mode toggle */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Mode</CardTitle>
            <CardDescription>Choose light or dark base.</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <button
              onClick={() => mode !== "light" && toggleMode()}
              data-testid="theme-mode-light"
              className={`flex-1 rounded-xl border-2 p-4 text-left transition-all ${
                mode === "light"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <Sun strokeWidth={1.5} className="size-5 mb-2" />
              <div className="font-semibold">Light</div>
              <div className="text-xs text-muted-foreground">Bright surfaces.</div>
            </button>
            <button
              onClick={() => mode !== "dark" && toggleMode()}
              data-testid="theme-mode-dark"
              className={`flex-1 rounded-xl border-2 p-4 text-left transition-all ${
                mode === "dark"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <Moon strokeWidth={1.5} className="size-5 mb-2" />
              <div className="font-semibold">Dark</div>
              <div className="text-xs text-muted-foreground">Dim, low-glare surfaces.</div>
            </button>
          </CardContent>
        </Card>

        {/* Palettes */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Palettes</CardTitle>
            <CardDescription>Curated color sets to suit your mood.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PALETTES.map((p) => {
                const active = palette === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPalette(p.id)}
                    data-testid={`theme-selector-${p.id}`}
                    className={`group rounded-2xl border-2 p-4 text-left transition-all ${
                      active
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="size-7 rounded-full border border-border"
                        style={{ background: `hsl(${p.swatch})` }}
                      />
                      {active && (
                        <Check strokeWidth={2} className="size-4 text-primary ml-auto" />
                      )}
                    </div>
                    <div className="font-semibold text-sm">{p.label}</div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Background patterns */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Background pattern</CardTitle>
            <CardDescription>
              Soft, gentle textures designed for calm focus. Pick one or leave it plain.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {PATTERNS.map((p) => {
                const active = pattern === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPattern(p.id)}
                    data-testid={`pattern-selector-${p.id}`}
                    className={`group rounded-2xl border-2 p-3 text-left transition-all relative overflow-hidden ${
                      active
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div
                      className="h-16 rounded-lg border border-border bg-card mb-2 relative overflow-hidden pattern-preview"
                      data-pattern={p.id}
                    />
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-sm">{p.label}</div>
                      {active && (
                        <Check strokeWidth={2} className="size-4 text-primary ml-auto" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Patterns are applied subtly behind the whole app — they shouldn't distract you.
            </p>
          </CardContent>
        </Card>


        {/* Custom */}
        <Card
          className={`rounded-2xl ${
            palette === "custom" ? "border-primary ring-2 ring-primary/10" : ""
          }`}
        >
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg font-display">Custom</CardTitle>
                <CardDescription>
                  Pick any primary and background color — full rainbow + hex.
                </CardDescription>
              </div>
              {palette === "custom" && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary text-primary-foreground font-bold">
                  Active
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <ColorField
                label="Primary"
                value={customPrimary}
                onChange={setCustomPrimary}
                testId="custom-primary"
              />
              <ColorField
                label="Background"
                value={customBg}
                onChange={setCustomBg}
                testId="custom-background"
              />
            </div>

            <Separator />

            {/* Save current as a named preset */}
            <div className="space-y-3" data-testid="save-custom-theme">
              <div className="flex items-center gap-2">
                <div className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Save this combo
                </div>
                {!isPremium && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                    <Crown strokeWidth={1.8} className="size-3" /> Premium
                  </span>
                )}
              </div>
              <div className="flex items-stretch gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveCurrentAsTheme()}
                  placeholder={
                    isPremium
                      ? "Name your theme — e.g. 'My morning palette'"
                      : "Upgrade to save your own theme combos"
                  }
                  maxLength={40}
                  disabled={!isPremium}
                  className="h-11 rounded-xl"
                  data-testid="save-theme-name-input"
                />
                <Button
                  onClick={isPremium ? saveCurrentAsTheme : () => navigate("/upgrade")}
                  disabled={savingTheme || (isPremium && !newName.trim())}
                  className="rounded-xl h-11 shrink-0"
                  data-testid="save-theme-button"
                >
                  {isPremium ? (
                    <>
                      <BookmarkPlus strokeWidth={1.5} className="size-4 mr-1.5" />
                      {savingTheme ? "Saving…" : "Save"}
                    </>
                  ) : (
                    <>
                      <Crown strokeWidth={1.5} className="size-4 mr-1.5" />
                      Upgrade
                    </>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {isPremium
                  ? "Saved themes appear below the presets and stay with your account on every device."
                  : "Start a 14-day free trial to save your custom palettes across devices."}
              </p>
            </div>

            <Separator />

            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-3">
                Quick presets
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PRESETS.map((preset) => {
                  const swapped = { p: preset.b, b: preset.p, label: preset.label };
                  const active =
                    palette === "custom" &&
                    customPrimary.toLowerCase() === preset.p.toLowerCase() &&
                    customBg.toLowerCase() === preset.b.toLowerCase();
                  const activeSwapped =
                    palette === "custom" &&
                    customPrimary.toLowerCase() === preset.b.toLowerCase() &&
                    customBg.toLowerCase() === preset.p.toLowerCase();
                  return (
                    <div
                      key={preset.label}
                      data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`relative rounded-xl border-2 transition-all ${
                        active || activeSwapped
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <button
                        onClick={() => applyPreset(activeSwapped ? swapped : preset)}
                        className="w-full p-3 text-left"
                      >
                        <div className="flex gap-1 mb-2">
                          <span
                            className="size-5 rounded-full border border-border"
                            style={{ background: activeSwapped ? preset.b : preset.p }}
                          />
                          <span
                            className="size-5 rounded-full border border-border"
                            style={{ background: activeSwapped ? preset.p : preset.b }}
                          />
                        </div>
                        <div className="text-xs font-semibold pr-7 truncate">{preset.label}</div>
                      </button>
                      <button
                        onClick={() => applyPreset(activeSwapped ? preset : swapped)}
                        aria-label="Swap primary and background colors"
                        title="Swap colors"
                        className="absolute top-2 right-2 size-6 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
                        data-testid={`preset-swap-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <RefreshCcw strokeWidth={1.8} className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {savedThemes.length > 0 && (
              <div data-testid="saved-themes-section">
                <div className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-3">
                  Your saved themes
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {savedThemes.map((theme) => {
                    const active =
                      palette === "custom" &&
                      customPrimary.toLowerCase() === theme.primary_hex.toLowerCase() &&
                      customBg.toLowerCase() === theme.background_hex.toLowerCase();
                    return (
                      <div
                        key={theme.id}
                        data-testid={`saved-theme-${theme.id}`}
                        className={`relative rounded-xl border-2 p-3 transition-all ${
                          active
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border hover:border-primary/40"
                        }`}
                      >
                        <button
                          onClick={() => applySavedTheme(theme)}
                          className="w-full text-left"
                          data-testid={`saved-theme-apply-${theme.id}`}
                        >
                          <div className="flex gap-1 mb-2">
                            <span
                              className="size-5 rounded-full border border-border"
                              style={{ background: theme.primary_hex }}
                            />
                            <span
                              className="size-5 rounded-full border border-border"
                              style={{ background: theme.background_hex }}
                            />
                          </div>
                          <div className="text-xs font-semibold pr-6 truncate">{theme.name}</div>
                        </button>
                        <button
                          onClick={() => deleteSavedTheme(theme.id)}
                          aria-label="Delete saved theme"
                          className="absolute top-2 right-2 size-6 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
                          data-testid={`saved-theme-delete-${theme.id}`}
                        >
                          <Trash2 strokeWidth={1.5} className="size-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border bg-background p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-3">
                Preview
              </div>
              <div className="flex items-center gap-3">
                <Button className="rounded-xl">Primary button</Button>
                <Button variant="outline" className="rounded-xl">
                  Outline
                </Button>
                <span className="text-sm text-muted-foreground">Body text</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
